/**
 * Subscription Analytics Tab
 * Dashboard with stats, charts, and recent events
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle2,
  CreditCard,
  Database,
  Eye,
  MousePointerClick,
  RefreshCw,
  Signal,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import clsx from 'clsx';
import { useI18n } from '@/i18n';

// Types
interface SubscriptionSummary {
  total_users: number;
  total_subscribers: number;
  trial_users: number;
  active_users: number;
  expired_users: number;
  cancelled_users: number;
  trial_to_paid_rate: number;
  churn_rate: number;
}

interface StatusDistribution {
  none: number;
  trial: number;
  active: number;
  expired: number;
  cancelled: number;
}

interface ProductDistribution {
  [product_id: string]: number;
}

interface DailyStat {
  date: string;
  new_trials: number;
  conversions: number;
  cancellations: number;
  new_subscriptions: number;
}

interface RecentEvent {
  user_id: string;
  display_name: string | null;
  event_type: string;
  previous_status: string | null;
  new_status: string | null;
  product_id: string | null;
  created_at: string;
}

interface PaywallAttributionSummary {
  server_signals: number;
  unique_signal_users: number;
  client_views: number | null;
  unique_view_users: number | null;
  purchase_starts: number | null;
  purchase_completions: number | null;
  dismissals: number | null;
  view_rate: number | null;
  start_rate: number | null;
  completion_rate: number | null;
  posthog_source: 'posthog' | 'unavailable';
  notes: string[];
}

interface PaywallTriggerBreakdown {
  trigger: string;
  server_signals: number;
  unique_signal_users: number;
  client_views: number | null;
  purchase_starts: number | null;
  purchase_completions: number | null;
  view_rate: number | null;
  completion_rate: number | null;
}

interface PaywallDailyAttribution {
  date: string;
  server_signals: number;
  client_views: number | null;
  purchase_starts: number | null;
  purchase_completions: number | null;
}

interface RecentPaywallSignal {
  id: string;
  user_id: string;
  topic_id: string | null;
  stream_id: string | null;
  trigger: string;
  reason: string | null;
  created_at: string;
}

interface PaywallAttributionAnalytics {
  summary: PaywallAttributionSummary;
  by_trigger: PaywallTriggerBreakdown[];
  daily: PaywallDailyAttribution[];
  recent_signals: RecentPaywallSignal[];
}

interface SubscriptionAnalytics {
  summary: SubscriptionSummary;
  status_distribution: StatusDistribution;
  product_distribution: ProductDistribution;
  daily_stats: DailyStat[];
  recent_events: RecentEvent[];
  paywall_attribution?: PaywallAttributionAnalytics | null;
}

// API functions
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

async function fetchAnalytics(startDate?: string, endDate?: string): Promise<SubscriptionAnalytics> {
  const token = localStorage.getItem('admin_token');
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);

  const response = await fetch(
    `${API_BASE}/v1/subscription/admin/analytics?${params}`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error('Failed to fetch analytics');
  return response.json();
}

// Helper components
function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  trendLabel,
  color = 'primary',
}: {
  title: string;
  value: string | number;
  icon: typeof Users;
  trend?: number;
  trendLabel?: string;
  color?: 'primary' | 'green' | 'yellow' | 'red' | 'blue';
}) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={clsx(
            'flex items-center text-sm font-medium',
            trend >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{title}</div>
        {trendLabel && <div className="text-xs text-gray-400 mt-1">{trendLabel}</div>}
      </div>
    </div>
  );
}

function StatusBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{value} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full', color)} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function formatNullableNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? '--' : value.toLocaleString();
}

function formatNullableRate(value: number | null | undefined): string {
  return value === null || value === undefined ? '--' : `${value.toFixed(1)}%`;
}

function triggerLabel(trigger: string): string {
  const labels: Record<string, string> = {
    smart: '智能引导',
    quota: '深度额度',
    fallback: '传统额度',
    launch_auto: '自动展示',
    resume_auto: '恢复展示',
    manual: '手动打开',
    unknown: '未知来源',
  };
  return labels[trigger] || trigger;
}

function triggerBadgeClass(trigger: string): string {
  const colors: Record<string, string> = {
    smart: 'bg-blue-50 text-blue-700 border-blue-100',
    quota: 'bg-amber-50 text-amber-700 border-amber-100',
    fallback: 'bg-rose-50 text-rose-700 border-rose-100',
    launch_auto: 'bg-gray-50 text-gray-700 border-gray-200',
    resume_auto: 'bg-gray-50 text-gray-700 border-gray-200',
    manual: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return colors[trigger] || 'bg-gray-50 text-gray-700 border-gray-200';
}

function PaywallMetricCard({
  title,
  value,
  helper,
  rate,
  icon: Icon,
  color,
}: {
  title: string;
  value: string;
  helper: string;
  rate?: string;
  icon: typeof Signal;
  color: 'blue' | 'green' | 'amber' | 'indigo';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {rate && <span className="text-xs font-medium text-gray-500">{rate}</span>}
      </div>
      <div className="mt-3">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm font-medium text-gray-700">{title}</div>
        <div className="mt-1 text-xs text-gray-400">{helper}</div>
      </div>
    </div>
  );
}

function PaywallAttributionSection({ data }: { data?: PaywallAttributionAnalytics | null }) {
  if (!data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900">付费墙归因</h3>
        <p className="mt-2 text-sm text-gray-500">暂无付费墙归因数据。</p>
      </div>
    );
  }

  const { summary } = data;
  const funnel = [
    { label: '后端发起', value: summary.server_signals },
    { label: '实际展示', value: summary.client_views },
    { label: '开始购买', value: summary.purchase_starts },
    { label: '购买成功', value: summary.purchase_completions },
  ];
  const maxDaily = Math.max(
    1,
    ...data.daily.map((row) => Math.max(row.server_signals, row.client_views ?? 0, row.purchase_starts ?? 0))
  );
  const recentDaily = data.daily.slice(-7);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">付费墙归因</h3>
          <p className="mt-1 text-sm text-gray-500">
            后端发起来自 Postgres，展示与购买行为来自 PostHog
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600">
          <Database className="h-3.5 w-3.5" />
          <span>{summary.posthog_source === 'posthog' ? 'Server + PostHog' : 'Server only'}</span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <PaywallMetricCard
          title="后端发起"
          value={summary.server_signals.toLocaleString()}
          helper="paywall_signal_events"
          icon={Signal}
          color="blue"
        />
        <PaywallMetricCard
          title="实际展示"
          value={formatNullableNumber(summary.client_views)}
          helper="paywall_viewed"
          rate={`展示率 ${formatNullableRate(summary.view_rate)}`}
          icon={Eye}
          color="green"
        />
        <PaywallMetricCard
          title="开始购买"
          value={formatNullableNumber(summary.purchase_starts)}
          helper="subscription_started"
          rate={formatNullableRate(summary.start_rate)}
          icon={MousePointerClick}
          color="amber"
        />
        <PaywallMetricCard
          title="购买成功"
          value={formatNullableNumber(summary.purchase_completions)}
          helper="subscription_completed"
          rate={formatNullableRate(summary.completion_rate)}
          icon={CheckCircle2}
          color="indigo"
        />
      </div>

      <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {funnel.map((item, index) => (
            <div key={item.label} className="flex items-center gap-3">
              <div>
                <div className="font-semibold text-gray-900">{formatNullableNumber(item.value)}</div>
                <div className="text-xs text-gray-500">{item.label}</div>
              </div>
              {index < funnel.length - 1 && <ArrowRight className="h-4 w-4 text-gray-400" />}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">触发类型</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">后端发起</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">实际展示</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">开始购买</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">购买成功</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">展示率</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">成功率</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.by_trigger.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                    暂无触发类型数据
                  </td>
                </tr>
              ) : (
                data.by_trigger.map((row) => (
                  <tr key={row.trigger} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm">
                      <span className={clsx('inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium', triggerBadgeClass(row.trigger))}>
                        {triggerLabel(row.trigger)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">{row.server_signals}</td>
                    <td className="px-3 py-3 text-right text-sm text-gray-700">{formatNullableNumber(row.client_views)}</td>
                    <td className="px-3 py-3 text-right text-sm text-gray-700">{formatNullableNumber(row.purchase_starts)}</td>
                    <td className="px-3 py-3 text-right text-sm text-gray-700">{formatNullableNumber(row.purchase_completions)}</td>
                    <td className="px-3 py-3 text-right text-sm text-gray-700">{formatNullableRate(row.view_rate)}</td>
                    <td className="px-3 py-3 text-right text-sm text-gray-700">{formatNullableRate(row.completion_rate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-900">近 7 日趋势</h4>
            </div>
            <span className="text-xs text-gray-400">发起 / 展示 / 开始</span>
          </div>
          <div className="space-y-3">
            {recentDaily.length === 0 ? (
              <p className="py-5 text-center text-sm text-gray-500">暂无趋势数据</p>
            ) : (
              recentDaily.map((row) => (
                <div key={row.date}>
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                    <span>{formatDateForMini(row.date)}</span>
                    <span>
                      {row.server_signals} / {formatNullableNumber(row.client_views)} / {formatNullableNumber(row.purchase_starts)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <div className="h-2 rounded bg-blue-100">
                      <div className="h-2 rounded bg-blue-500" style={{ width: `${(row.server_signals / maxDaily) * 100}%` }} />
                    </div>
                    <div className="h-2 rounded bg-green-100">
                      <div className="h-2 rounded bg-green-500" style={{ width: `${((row.client_views ?? 0) / maxDaily) * 100}%` }} />
                    </div>
                    <div className="h-2 rounded bg-amber-100">
                      <div className="h-2 rounded bg-amber-500" style={{ width: `${((row.purchase_starts ?? 0) / maxDaily) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {(summary.posthog_source === 'unavailable' || summary.notes.length > 0) && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            {summary.notes[0] || 'PostHog 不可用时，行为指标显示为 --，后端发起仍可用。'}
          </span>
        </div>
      )}
    </div>
  );
}

function formatDateForMini(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
  });
}

export function SubscriptionAnalyticsTab() {
  const { t } = useI18n();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { start: new Date(now.setDate(now.getDate() - 7)).toISOString() };
      case '30d':
        return { start: new Date(now.setDate(now.getDate() - 30)).toISOString() };
      case '90d':
        return { start: new Date(now.setDate(now.getDate() - 90)).toISOString() };
      default:
        return {};
    }
  };

  const { start } = getDateRange();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-analytics', dateRange],
    queryFn: () => fetchAnalytics(start),
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEventBadge = (eventType: string) => {
    const badges: Record<string, { labelKey: string; color: string }> = {
      'trial_started': { labelKey: 'subscriptionPage.analytics.eventTypes.trialStarted', color: 'bg-blue-100 text-blue-700' },
      'subscription_created': { labelKey: 'subscriptionPage.analytics.eventTypes.subscribed', color: 'bg-green-100 text-green-700' },
      'subscription_renewed': { labelKey: 'subscriptionPage.analytics.eventTypes.renewed', color: 'bg-green-100 text-green-700' },
      'subscription_cancelled': { labelKey: 'subscriptionPage.analytics.eventTypes.cancelled', color: 'bg-red-100 text-red-700' },
      'subscription_expired': { labelKey: 'subscriptionPage.analytics.eventTypes.expired', color: 'bg-yellow-100 text-yellow-700' },
      'admin_override': { labelKey: 'subscriptionPage.analytics.eventTypes.admin', color: 'bg-purple-100 text-purple-700' },
    };
    const badge = badges[eventType];
    const label = badge ? t(badge.labelKey) : eventType;
    const color = badge ? badge.color : 'bg-gray-100 text-gray-700';
    return (
      <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', color)}>
        {label}
      </span>
    );
  };

  return (
    <div>
      {/* Header with Date Range and Refresh */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                dateRange === range
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {range === 'all' ? t('subscriptionPage.analytics.dateRanges.allTime') :
               range === '7d' ? t('subscriptionPage.analytics.dateRanges.days7') :
               range === '30d' ? t('subscriptionPage.analytics.dateRanges.days30') :
               t('subscriptionPage.analytics.dateRanges.days90')}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('subscriptionPage.analytics.refresh')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-semibold">{t('subscriptionPage.analytics.errorLoading')}</h3>
            <p className="text-red-600 text-sm">{(error as Error).message}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Analytics Content */}
      {data && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <StatCard
              title={t('subscriptionPage.analytics.totalUsers')}
              value={data.summary.total_users.toLocaleString()}
              icon={Users}
              color="primary"
            />
            <StatCard
              title={t('subscriptionPage.analytics.activeSubscribers')}
              value={data.summary.active_users.toLocaleString()}
              icon={CreditCard}
              color="green"
            />
            <StatCard
              title={t('subscriptionPage.analytics.trialToPaidRate')}
              value={`${data.summary.trial_to_paid_rate.toFixed(1)}%`}
              icon={TrendingUp}
              color="blue"
            />
            <StatCard
              title={t('subscriptionPage.analytics.churnRate')}
              value={`${data.summary.churn_rate.toFixed(1)}%`}
              icon={TrendingDown}
              color="red"
            />
          </div>

          <PaywallAttributionSection data={data.paywall_attribution} />

          <div className="grid grid-cols-2 gap-6">
            {/* Status Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.statusDistribution')}</h3>
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.free')}
                value={data.status_distribution.none}
                total={data.summary.total_users}
                color="bg-gray-400"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.trial')}
                value={data.status_distribution.trial}
                total={data.summary.total_users}
                color="bg-blue-500"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.active')}
                value={data.status_distribution.active}
                total={data.summary.total_users}
                color="bg-green-500"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.expired')}
                value={data.status_distribution.expired}
                total={data.summary.total_users}
                color="bg-yellow-500"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.cancelled')}
                value={data.status_distribution.cancelled}
                total={data.summary.total_users}
                color="bg-red-500"
              />
            </div>

            {/* Product Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.productDistribution')}</h3>
              {Object.keys(data.product_distribution).length === 0 ? (
                <p className="text-gray-500 text-sm">{t('subscriptionPage.analytics.noProductData')}</p>
              ) : (
                Object.entries(data.product_distribution).map(([product, count]) => (
                  <StatusBar
                    key={product}
                    label={product}
                    value={count}
                    total={data.summary.active_users}
                    color="bg-primary-500"
                  />
                ))
              )}
            </div>
          </div>

          {/* Daily Stats */}
          {data.daily_stats.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.dailyTrends')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.date')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.newTrials')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.conversions')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.newSubscriptions')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.cancellations')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.daily_stats.slice(0, 14).map((stat) => (
                      <tr key={stat.date} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">{formatDate(stat.date)}</td>
                        <td className="px-4 py-2 text-sm text-right text-blue-600">{stat.new_trials}</td>
                        <td className="px-4 py-2 text-sm text-right text-green-600">{stat.conversions}</td>
                        <td className="px-4 py-2 text-sm text-right text-primary-600">{stat.new_subscriptions}</td>
                        <td className="px-4 py-2 text-sm text-right text-red-600">{stat.cancellations}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Events */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.recentEvents')}</h3>
            {data.recent_events.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('subscriptionPage.analytics.noRecentEvents')}</p>
            ) : (
              <div className="space-y-3">
                {data.recent_events.map((event, index) => (
                  <div
                    key={`${event.user_id}-${event.created_at}-${index}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {event.display_name || event.user_id.slice(0, 8)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {event.product_id && <span className="mr-2">{event.product_id}</span>}
                          {formatDateTime(event.created_at)}
                        </div>
                      </div>
                    </div>
                    {getEventBadge(event.event_type)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
